import { motion } from "framer-motion";
import { GraduationCap, MapPin, Mail, Loader2 } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@kuratorium.local");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#0B1120]">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 lg:p-14 relative grain">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-10">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight text-slate-50">Kuratorium Jobs</div>
              <div className="text-xs text-slate-500">Agregator ofert oświatowych</div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-50 leading-tight">
            Zaloguj się
            <span className="block text-cyan-400 text-2xl sm:text-3xl mt-3">do panelu.</span>
          </h1>
          <p className="text-slate-400 mt-4 text-sm leading-relaxed">
            Aplikacja jedno-użytkownikowa. Użyj danych z pliku <code className="text-cyan-400">.env</code>.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</Label>
              <Input
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 bg-slate-900/60 border-slate-800 focus-visible:ring-cyan-400 focus-visible:ring-offset-slate-950"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Hasło</Label>
              <Input
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 bg-slate-900/60 border-slate-800 focus-visible:ring-cyan-400 focus-visible:ring-offset-slate-950"
              />
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold rounded-lg transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Zaloguj się"}
            </Button>
          </form>

          <div className="mt-14 grid grid-cols-3 gap-4 text-xs text-slate-500">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-slate-300 font-medium">Filtr km</div>
                <div>promień lokalizacji</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-slate-300 font-medium">Predyspozycje</div>
                <div>przedmioty i poziom</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-cyan-400 mt-0.5" />
              <div>
                <div className="text-slate-300 font-medium">Digest</div>
                <div>codzienny mail</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right: image with overlay */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZWFjaGVyJTIwY2xhc3Nyb29tJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg2NTczMjgzfDA&ixlib=rb-4.1.0&q=85"
          alt="Nauczyciel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/70 to-cyan-950/80" />
        <div className="absolute inset-0 flex items-end p-14">
          <div>
            <div className="text-cyan-400 text-xs uppercase tracking-[0.3em] font-semibold mb-4">
              Mazowieckie · MVP
            </div>
            <div className="font-display text-3xl xl:text-4xl font-semibold text-slate-50 leading-tight max-w-md">
              Praca w oświacie, bez przeklikiwania przez dziesiątki stron kuratorium.
            </div>
            <div className="text-slate-400 mt-4 text-sm max-w-md">
              Skanujemy portale, filtrujemy po Twoich przedmiotach i wysyłamy Ci codzienny digest.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
